import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Play } from '@hivebrite/design-system/Play';

import { DefaultLink } from 'hb-react/frontoffice/components/Link';

import DisplayWrapperImage from './DisplayWrapperImage';
import DisplayWrapperNoImage from './DisplayWrapperNoImage';

interface PreviewProps {
  aspectRatio: number;
  autoplay?: boolean;
  customRenderForEmptyImage: (embed: any) => JSX.Element;
  embed: any;
  externalUrl: string;
  isDisabled: boolean;
  isEmbedVideoOnHtml: boolean;
  isVimeoWhitelistVideo: boolean;
  openPlayVideo: () => void;
  roundedEmbed?: boolean;
}

const Preview = ({
  aspectRatio,
  autoplay = false,
  customRenderForEmptyImage,
  embed,
  externalUrl,
  isDisabled,
  isEmbedVideoOnHtml,
  isVimeoWhitelistVideo,
  openPlayVideo,
  roundedEmbed = true,
}: PreviewProps) => {
  const [playingVideo, setPlayingVideo] = useState(autoplay);

  const handlePlayVideo = openPlayVideo || (() => setPlayingVideo(true));

  const { html, thumbnail_url: thumbnailUrl } = embed;

  // vimeo whitelist videos have no thumbnail_url but we still want to display the video
  if (isVimeoWhitelistVideo) {
    return (
      <Box flex={1}>
        <WrapperVideo
          $aspectRatio={aspectRatio}
          $isDisabled={isDisabled}
          $rounded={roundedEmbed}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {!playingVideo && (
          <Glimmer $rounded={roundedEmbed}>
            <Play
              ariaLabel={__('Start the video preview')}
              onClick={handlePlayVideo}
            />
          </Glimmer>
        )}
      </Box>
    );
  }

  if (!thumbnailUrl) {
    // If no img preview URL, display custom render (if passed) or nothing
    return customRenderForEmptyImage ? (
      <DisplayWrapperNoImage rounded={roundedEmbed}>
        {customRenderForEmptyImage(embed)}
      </DisplayWrapperNoImage>
    ) : null;
  }

  // Display Video preview img
  if (isEmbedVideoOnHtml && playingVideo === false) {
    return (
      <DisplayWrapperImage
        aspectRatio={aspectRatio}
        embed={embed}
        rounded={roundedEmbed}
      >
        <Glimmer $rounded={roundedEmbed}>
          <Play
            ariaLabel={__('Start the video preview')}
            onClick={handlePlayVideo}
          />
        </Glimmer>
      </DisplayWrapperImage>
    );
  }

  // Display Embed Video (user clicked on video preview img)
  if (isEmbedVideoOnHtml && playingVideo === true) {
    return (
      <WrapperVideo
        $aspectRatio={aspectRatio}
        $isDisabled={isDisabled}
        $rounded={roundedEmbed}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  // Display Website preview img
  return (
    // @ts-expect-error: bad types
    <DefaultLink
      aria-hidden="true"
      href={externalUrl ?? embed.url}
      style={{ width: '100%' }}
      tabIndex={-1}
      target="_blank"
    >
      <DisplayWrapperImage
        aspectRatio={aspectRatio}
        embed={embed}
        rounded={roundedEmbed}
      />
    </DefaultLink>
  );
};

export default Preview;

const WrapperVideo = styled(Box).attrs<{
  $aspectRatio: PreviewProps['aspectRatio'];
}>(({ $aspectRatio }) => {
  let attrs = { display: 'block' };

  if (!$aspectRatio) {
    attrs = {
      ...attrs,
    };
  }

  return attrs;
})<{
  $rounded: PreviewProps['roundedEmbed'];
  $aspectRatio: PreviewProps['aspectRatio'];
  $isDisabled: PreviewProps['isDisabled'];
}>`
  background-color: #000;
  text-align: center;
  width: 100%;
  height: 100%;
  border-radius: ${({ $rounded }) =>
    $rounded === true ? '4px 4px 0px 0px' : $rounded || '0px'};

  ${({ $aspectRatio }) =>
    $aspectRatio
      ? css`
          aspect-ratio: ${$aspectRatio};
        `
      : ''}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.7;
      pointer-events: none;
    `}

  iframe {
    border-radius: ${({ $rounded }) =>
      $rounded === true ? '4px 4px 0px 0px' : $rounded || '0px'};
    width: 100%;
    height: 100%;
  }
`;

const Glimmer = styled(Box)<{ $rounded: PreviewProps['roundedEmbed'] }>`
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  border-radius: ${({ $rounded }) =>
    $rounded === true ? '4px 4px 0px 0px' : $rounded || '0px'};
`;
